import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Image, Box, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import CategoryForm from '../forms/CategoryForm';
import { notifications } from '@mantine/notifications';
import { getCategories, deleteCategory } from '../../services/api.service';
import { modals } from '@mantine/modals';
import DeleteButton from '../buttons/DeleteButton';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
	imagePreview: {
		padding: 3,
		border: `1px solid ${theme.colorScheme === 'dark' ? theme.white : theme.black}`,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.colorScheme === 'dark' ? theme.white : theme.black,
	}
}));

const Categories = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [categories, setCategories] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [categoryEdit, setCategoryEdit] = useState({});
	const [filteredData, setFilteredData] = useState([]);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getCategoriesList();
		// eslint-disable-next-line
	}, []);

	// Get categories
	const getCategoriesList = () => {

		// Get
		getCategories(function (response) {

			// Set categories and initial sorted data, remove loader
			setCategories(response.data);
			setFilteredData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Nombre
			</Th>
			<Th>
				Imagen
			</Th>
			<Th></Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => toggleCategoryEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.name}
			</td>
			<td width={150}>
				<Image
					src={element.image}
					className={classes.imagePreview}
					radius={theme.radius.sm}
					maw={100}
				/>
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(categories, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query))));
	}

	// Close categories modal
	const closeModal = () => {
		// Get categories and close modal
		getCategoriesList();
		setIsEdit(false);
		setCategoryEdit({});
		close();
	}

	// Toggle category edit
	const toggleCategoryEdit = (event, category) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setCategoryEdit(category);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Categoría',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar esta categoría? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar categoría', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeleteCategory(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete category
	const triggerDeleteCategory = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteCategory(id, function () {

			// Show success
			notifications.show({
				title: 'Categoría eliminada correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Category Form */}
				<CategoryForm
					closeModal={closeModal}
					isEdit={isEdit}
					category={categoryEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Categorías
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Category */}
							<Button
								onClick={open}
								variant="filled"
							>
								Nueva Categoría
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar categorías"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={3}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default Categories;
